import { Check, Copy, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { GalleryItem } from "../shared";

interface DetailModalProps {
  item: GalleryItem;
  onClose: () => void;
  onNavigate: (direction: "previous" | "next") => void;
}

export function DetailModal({ item, onClose, onNavigate }: DetailModalProps) {
  const [copied, setCopied] = useState(false);
  const [loadedImage, setLoadedImage] = useState<string | null>(null);
  const desktopCloseButton = useRef<HTMLButtonElement>(null);
  const mobileCloseButton = useRef<HTMLButtonElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const prompt = useRef<HTMLParagraphElement>(null);
  const closeHandler = useRef(onClose);
  const navigateHandler = useRef(onNavigate);
  const imageReady = loadedImage === item.imageUrl;

  useEffect(() => {
    closeHandler.current = onClose;
    navigateHandler.current = onNavigate;
  }, [onClose, onNavigate]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const mobileLayout = window.matchMedia("(max-width: 820px)").matches;
    (mobileLayout ? mobileCloseButton.current : desktopCloseButton.current)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeHandler.current();
      const target = event.target;
      const editingText = target instanceof HTMLElement && (
        target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
      );
      if (editingText || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        navigateHandler.current(event.key === "ArrowLeft" ? "previous" : "next");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    setCopied(false);
    backdrop.current?.scrollTo({ top: 0 });
    if (prompt.current) prompt.current.scrollTop = 0;
  }, [item.id]);

  const copyPrompt = () => {
    if (!item.prompt) return;
    const fallback = document.createElement("textarea");
    fallback.value = item.prompt;
    fallback.setAttribute("readonly", "");
    fallback.style.position = "fixed";
    fallback.style.opacity = "0";
    document.body.appendChild(fallback);
    fallback.select();
    const copiedSynchronously = document.execCommand("copy");
    fallback.remove();
    if (!copiedSynchronously && navigator.clipboard) {
      void navigator.clipboard.writeText(item.prompt).catch(() => undefined);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <>
      <div ref={backdrop} className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
        <section className="detail-modal" role="dialog" aria-modal="true" aria-labelledby="detail-title">
          <button ref={desktopCloseButton} className="icon-button modal-close desktop-modal-close" type="button" onClick={onClose} aria-label="Close details">
            <X size={21} />
          </button>
          <div className={`detail-image-wrap${imageReady ? " is-ready" : ""}`} aria-busy={!imageReady}>
            <span className="detail-image-skeleton" aria-hidden="true" />
            <img
              key={item.imageUrl}
              src={item.imageUrl}
              alt={item.title || "Gallery artwork"}
              className={`detail-image${imageReady ? " is-ready" : ""}`}
              style={{ aspectRatio: `${item.width} / ${item.height}` }}
              onLoad={() => setLoadedImage(item.imageUrl)}
              onError={() => setLoadedImage(item.imageUrl)}
            />
          </div>
          <div className="detail-copy">
            <p className="eyebrow">Nana Republic / Archive</p>
            <h2 id="detail-title">{item.title || "Untitled"}</h2>
            {item.tags.length > 0 && <div className="detail-tags" aria-label="Tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}
            {item.prompt ? (
              <div className="prompt-panel">
                <div className="prompt-toolbar">
                  <span>Prompt</span>
                  <button type="button" className="copy-button" onClick={copyPrompt}>
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <p ref={prompt}>{item.prompt}</p>
              </div>
            ) : (
              <p className="muted-copy">No prompt was added to this image.</p>
            )}
          </div>
        </section>
      </div>
      <button ref={mobileCloseButton} className="icon-button mobile-modal-close" type="button" onClick={onClose} aria-label="Close details">
        <X size={21} />
      </button>
    </>
  );
}
